import {
  createContext,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useContext,
} from "react";

type WorkspaceLayoutMode = "chat" | "application" | "split";

export type SimonPanelView = "conversation" | "all-chats";

export interface WorkspaceRightPanel {
  title: string;
  actions?: ReactNode;
  body: ReactNode;
  onClose?: () => void;
}

export interface ChatContext {
  viewedSection?: string;
  entities: { id: string; type: string }[];
}

export interface ComposerSubmit {
  text: string;
  files?: readonly File[];
  onSettled?: (outcome: ComposerSubmitOutcome) => void;
}

export type ComposerSubmitOutcome =
  | { sent: true; turn: "complete" | "stopped" | "failed" | "lost" }
  | { sent: false; reason: "no-upload" | "refused" | "not-picked-up" };

export type IntroRequest = { from: "welcome" | "simon" | "capabilities" } | { step: string };

export interface WorkspaceContextValue {
  layoutMode: WorkspaceLayoutMode;
  chatVisible: boolean;
  applicationVisible: boolean;
  applicationAvailable: boolean;
  systemPage: boolean;
  showChat: (alongsideApplication?: boolean, options?: { replace?: boolean }) => void;
  showApplication: (path?: string, alongsideChat?: boolean) => void;
  toggleChatPanel: () => void;
  toggleApplicationPanel: () => void;
  simonPanelView: SimonPanelView;
  showSimonConversation: () => void;
  showAllChats: () => void;

  openClientProfile: (clientId: string) => void;

  leftPanelWidth: number;
  setLeftPanelWidth: Dispatch<SetStateAction<number>>;
  pendingComposerSubmit: ComposerSubmit | null;
  requestComposerSubmit: (request: ComposerSubmit) => void;
  consumeComposerSubmit: () => void;
  pendingComposerDraft: string | null;
  requestComposerDraft: (text: string) => void;
  consumeComposerDraft: () => void;
  showIntro: (request?: IntroRequest) => Promise<boolean>;

  openCreateClient: () => void;
  openCreateClientPrefilled: (prefill: Record<string, unknown>) => boolean;

  openRightPanel: (panel: WorkspaceRightPanel) => void;
  closeRightPanel: () => void;
  chatContext: ChatContext | null;
  setChatContext: (context: ChatContext | null) => void;
  setHomeSlotVisible: (visible: boolean) => void;
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

export function WorkspaceProvider({
  value,
  children,
}: {
  value: WorkspaceContextValue;
  children: ReactNode;
}) {
  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

export function useWorkspace(): WorkspaceContextValue {
  const context = useContext(WorkspaceContext);
  if (!context) throw new Error("useWorkspace must be used inside <WorkspaceProvider>");
  return context;
}
